import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Web as WebIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  FolderZip as ZipIcon,
  ViewCarousel as HeroIcon,
  Store as BusinessIcon,
  ViewList as GroupsIcon,
  Palette as PaletteIcon,
  Code as CodeIcon,
  HelpOutline as FaqIcon,
  Star as StarIcon,
  Check as CheckIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { api } from '../../services/api';
import ImageUploadCrop from '../../components/common/ImageUploadCrop';

interface HeroSlide {
  id: string;
  badge: string;
  titulo: string;
  subtitulo: string;
  cta_texto: string;
  cta_url: string;
  cta_secundario_texto: string;
  cta_secundario_url: string;
  recurso_1?: string;
  recurso_2?: string;
  recurso_3?: string;
  card_header?: string;
  card_speed?: string;
  card_unit?: string;
  card_price?: string;
  card_subtext?: string;
  imagem_url: string;
  imagem_fundo_url?: string;
  animar_imagem?: boolean;
  animar_imagem_fundo?: boolean;
  remover_gradiente_fundo?: boolean;
  desativar_borda?: boolean;
  estilo_fundo_card?: 'padrao' | 'transparente' | 'claro';
  inverter_lado?: boolean;
  aumentar_tamanho?: boolean;
  ativo: boolean;
  ordem: number;
}

interface FaqItem {
  id: string;
  pergunta: string;
  resposta: string;
}

interface DepoimentoItem {
  id: string;
  nome: string;
  cidade: string;
  foto: string;
  texto: string;
}

interface BeneficioItem {
  id: string;
  icone: string;
  titulo: string;
  descricao: string;
}

interface TemplateItem {
  id: string;
  name: string;
  description: string;
  author?: string;
  version?: string;
  type: 'builtin' | 'custom';
  folderName?: string;
  preview?: string;
}

interface PlanItem {
  id: number;
  nome: string;
  valor: number;
  velocidade_download: number;
  velocidade_upload: number;
  ativo: boolean;
  exibir_site: boolean;
  grupo_id: number;
}

interface PlanGroupItem {
  id: number;
  titulo: string;
  padrao: boolean;
  exibir_site: boolean;
  qtde?: number;
}

const ParametrosHomepage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingZip, setUploadingZip] = useState(false);

  // Configuração principal da Homepage
  const [info, setInfo] = useState({
    nome_empresa: '',
    slogan: '',
    logo_url: '',
    favicon_url: '',
    telefone: '',
    whatsapp: '',
    whatsapp_mensagem: '',
    email: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    horario_atendimento: '',
    link_central_assinante: '/central',
    link_segunda_via: '/2via',
    status_site: 'ativo',
    redes_sociais: {
      instagram: '',
      facebook: '',
      linkedin: '',
      youtube: '',
      tiktok: ''
    }
  });

  const [heros, setHeros] = useState<HeroSlide[]>([]);
  const [faq, setFaq] = useState<FaqItem[]>([]);
  const [depoimentos, setDepoimentos] = useState<DepoimentoItem[]>([]);
  const [beneficios, setBeneficios] = useState<BeneficioItem[]>([]);
  const [sections, setSections] = useState({
    heros_enabled: true,
    speed_test_enabled: true,
    planos_enabled: true,
    beneficios_enabled: true,
    sobre_enabled: true,
    depoimentos_enabled: true,
    faq_enabled: true,
    contato_enabled: true,
    app_section_enabled: true,
    whatsapp_float_enabled: true,
    destaque_plano_id: null as number | null,
    opcao_botao_contratar: 'whatsapp' as 'whatsapp' | 'precadastro' | 'desabilitado'
  });
  const [colors, setColors] = useState<{
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    card_bg: string;
    text: string;
    nav_bg?: string;
    plans_bg?: string;
    testimonials_bg?: string;
    faq_bg?: string;
  }>({
    primary: '#2563eb',
    secondary: '#0f172a',
    accent: '#3b82f6',
    background: '#f8fafc',
    card_bg: '#ffffff',
    text: '#0f172a',
    nav_bg: '',
    plans_bg: '',
    testimonials_bg: '',
    faq_bg: ''
  });
  const [sobre, setSobre] = useState({
    badge: 'QUEM SOMOS',
    titulo: 'Conectando Você ao Que Realmente Importa',
    subtitulo: 'Somos um provedor de internet de alta velocidade focado em levar fibra óptica dedicada de altíssima qualidade com atendimento humanizado e transparente.',
    texto: 'Fundado com o objetivo de revolucionar a experiência de conectividade na região, investimos continuamente na modernização de nossa infraestrutura de rede e equipamentos de última geração.',
    imagem_url: '',
    estatistica_1_numero: '99.9%',
    estatistica_1_rotulo: 'Disponibilidade de Rede',
    estatistica_2_numero: '+10 Mil',
    estatistica_2_rotulo: 'Clientes Conectados',
    estatistica_3_numero: '100%',
    estatistica_3_rotulo: 'Fibra Óptica Dedicada'
  });

  const [beneficiosSection, setBeneficiosSection] = useState({
    titulo: 'Por que escolher a internet da {EMPRESA}',
    imagem_url: '',
    beneficio_1_titulo: 'Ultravelocidade',
    beneficio_1_desc: 'Navegação com ultravelocidade e estabilidade você só encontra na nossa rede.',
    beneficio_2_titulo: 'Suporte Técnico',
    beneficio_2_desc: 'Qualidade e agilidade de atendimento com equipe treinada para oferecer a melhor solução para você.',
    beneficio_3_titulo: 'Sem Limites',
    beneficio_3_desc: 'Não existe qualquer tipo de franquia limitando a utilização da internet. Com a gente você não tem limites!',
    beneficio_4_titulo: 'Sem Travamentos',
    beneficio_4_desc: 'O melhor serviço para assistir séries, filmes, desenhos e todo conteúdo online.'
  });

  const [customCss, setCustomCss] = useState('');
  const [activeTemplate, setActiveTemplate] = useState<{ id: string; name: string; type: string }>({
    id: 'default-modern',
    name: 'Modern Fiber (Padrão)',
    type: 'builtin'
  });

  // Estados para modal de upload com crop
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState<{
    title: string;
    aspectRatio?: number;
    oldUrl?: string;
    onSave: (url: string) => void;
  } | null>(null);

  // Menus contextuais de upload (padrão ParametrosCentral)
  const [heroImgMenu, setHeroImgMenu] = useState<{ anchor: HTMLElement | null; heroId: string | null }>({
    anchor: null, heroId: null
  });
  const [heroFundoMenu, setHeroFundoMenu] = useState<{ anchor: HTMLElement | null; heroId: string | null }>({
    anchor: null, heroId: null
  });
  const [sobreMenu, setSobreMenu] = useState<null | HTMLElement>(null);
  const [beneficiosMenu, setBeneficiosMenu] = useState<null | HTMLElement>(null);
  const [depoimentoMenu, setDepoimentoMenu] = useState<{ anchor: HTMLElement | null; depId: string | null }>({
    anchor: null, depId: null
  });

  const openCropModal = (
    title: string,
    oldUrl: string | undefined,
    onSave: (url: string) => void,
    aspectRatio?: number
  ) => {
    setCropTarget({ title, oldUrl, onSave, aspectRatio });
    setCropModalOpen(true);
  };

  const handleCropImageSave = async (croppedImageBlob: Blob, croppedImageUrl: string) => {
    if (!cropTarget) return;

    try {
      const extension = croppedImageBlob.type === 'image/png' ? 'png' :
        croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([croppedImageBlob], `upload-${Date.now()}.${extension}`, {
        type: croppedImageBlob.type
      });

      const formData = new FormData();
      formData.append('image', file);
      if (cropTarget.oldUrl) {
        formData.append('old_url', cropTarget.oldUrl);
      }

      const toastId = toast.loading('Enviando imagem...');
      const res: any = await api.post('/homepage/upload-image', formData);
      toast.dismiss(toastId);

      if (res.success && res.data?.url) {
        toast.success('Imagem enviada com sucesso!');
        cropTarget.onSave(res.data.url);
        setCropModalOpen(false);
        setCropTarget(null);
      } else {
        toast.error('Falha ao enviar a imagem.');
      }
    } catch (error) {
      console.error('Erro no upload de imagem:', error);
      toast.error('Erro ao enviar imagem');
    }
  };

  // Lista de Grupos e Planos do Sistema
  const [grupos, setGrupos] = useState<PlanGroupItem[]>([]);
  const [planos, setPlanos] = useState<PlanItem[]>([]);

  // Lista de Templates
  const [templates, setTemplates] = useState<TemplateItem[]>([]);

  // Carregar dados da Homepage e Planos ao iniciar
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Carregar Config da Homepage
      const configRes: any = await api.get('/homepage/config');
      if (configRes && configRes.data) {
        const d = configRes.data;
        if (d.info) setInfo((prev) => ({ ...prev, ...d.info }));
        if (d.heros && Array.isArray(d.heros)) setHeros(d.heros);
        if (d.faq && Array.isArray(d.faq)) setFaq(d.faq);
        if (d.depoimentos && Array.isArray(d.depoimentos)) setDepoimentos(d.depoimentos);
        if (d.beneficios && Array.isArray(d.beneficios)) setBeneficios(d.beneficios);
        if (d.sobre) setSobre((prev) => ({ ...prev, ...d.sobre }));
        if (d.beneficios_section) setBeneficiosSection((prev) => ({ ...prev, ...d.beneficios_section }));
        if (d.sections) setSections((prev) => ({ ...prev, ...d.sections }));
        if (d.colors) setColors((prev) => ({ ...prev, ...d.colors }));
        if (d.customCss !== undefined) setCustomCss(d.customCss);
        if (d.activeTemplate) setActiveTemplate(d.activeTemplate);
      }

      // 2. Carregar Grupos de Planos
      const gruposRes: any = await api.get('/planos-grupos');
      if (gruposRes && gruposRes.data) {
        setGrupos(gruposRes.data);
      }

      // 3. Carregar Planos
      const planosRes: any = await api.get('/planos');
      if (planosRes && planosRes.data) {
        setPlanos(planosRes.data);
      }

      // 4. Carregar Templates
      const templatesRes: any = await api.get('/homepage/templates');
      if (templatesRes && templatesRes.data) {
        setTemplates(templatesRes.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do Homepage:', error);
      toast.error('Erro ao carregar configurações do site');
    } finally {
      setLoading(false);
    }
  };

  // Salvar Configurações Gerais da Homepage
  const handleSaveConfig = async (overrides?: {
    heros?: HeroSlide[];
    sobre?: any;
    beneficios_section?: any;
  }) => {
    setSaving(true);
    try {
      const payload = {
        info,
        heros: overrides?.heros ?? heros,
        sections,
        customCss,
        activeTemplate,
        faq,
        depoimentos,
        beneficios,
        sobre: overrides?.sobre ?? sobre,
        beneficios_section: overrides?.beneficios_section ?? beneficiosSection,
        colors
      };

      const res: any = await api.put('/homepage/config', payload);
      if (res.success) {
        toast.success('Configurações salvas com sucesso!');
      } else {
        toast.error(res.message || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar alterações da Homepage');
    } finally {
      setSaving(false);
    }
  };

  // Presets de Paleta de Cores (Combinações profissionais)
  const COLOR_PRESETS = [
    { id: 'red-speed-combo', name: 'Velocidade Vermelho & Azul', icon: '🚀', colors: { primary: '#dc2626', secondary: '#0066ff', accent: '#ef4444', background: '#f8fafc', card_bg: '#ffffff', text: '#0f172a' } },
    { id: 'dark-red-speed', name: 'Speed Red Dark', icon: '🚀', colors: { primary: '#ff2a4b', secondary: '#0077ff', accent: '#38bdf8', background: '#0a0f1d', card_bg: '#141d33', text: '#ffffff' } },
    { id: 'royal-blue-gold', name: 'Fibra Azul & Dourado', icon: '📶', colors: { primary: '#004ad7', secondary: '#f59e0b', accent: '#16a34a', background: '#f8fafc', card_bg: '#ffffff', text: '#0f172a' } },
    { id: 'dark-blue-gold', name: 'Royal Night Dark', icon: '📶', colors: { primary: '#2563eb', secondary: '#fbbf24', accent: '#22c55e', background: '#0b132b', card_bg: '#1c2541', text: '#ffffff' } },
    { id: 'orange-navy-vibe', name: 'Laranja & Marinho Provedor', icon: '🦊', colors: { primary: '#f25a0e', secondary: '#09184d', accent: '#0055d4', background: '#f8fafc', card_bg: '#ffffff', text: '#09184d' } },
    { id: 'dark-fox-vibe', name: 'Marinho Dark Prime', icon: '🦊', colors: { primary: '#ff6b1a', secondary: '#0b194d', accent: '#2563eb', background: '#070c1e', card_bg: '#0f1a3a', text: '#ffffff' } },
    { id: 'telecom-blue', name: 'Azul Telecom (Padrão)', icon: '🔵', colors: { primary: '#2563eb', secondary: '#0f172a', accent: '#3b82f6', background: '#f8fafc', card_bg: '#ffffff', text: '#0f172a' } },
    { id: 'neon-green', name: 'Verde Neon Fibra', icon: '🟢', colors: { primary: '#00e676', secondary: '#090d16', accent: '#00c853', background: '#090d16', card_bg: '#0f172a', text: '#ffffff' } },
    { id: 'cyber-orange', name: 'Laranja Cyberpunk', icon: '🟠', colors: { primary: '#ff6b00', secondary: '#1a1a24', accent: '#ff8800', background: '#121218', card_bg: '#1e1e28', text: '#ffffff' } },
    { id: 'purple-premium', name: 'Roxo Premium', icon: '🟣', colors: { primary: '#7c3aed', secondary: '#0f172a', accent: '#8b5cf6', background: '#f5f3ff', card_bg: '#ffffff', text: '#1e1b4b' } },
    { id: 'red-ultra', name: 'Vermelho Ultra', icon: '🔴', colors: { primary: '#dc2626', secondary: '#111827', accent: '#ef4444', background: '#fef2f2', card_bg: '#ffffff', text: '#1f2937' } },
    { id: 'cyan-hitech', name: 'Ciano Hi-Tech', icon: '🩵', colors: { primary: '#06b6d4', secondary: '#0f172a', accent: '#22d3ee', background: '#ecfeff', card_bg: '#ffffff', text: '#0e7490' } },
    { id: 'pink-synthwave', name: 'Pink Synthwave Gamer', icon: '💖', colors: { primary: '#ec4899', secondary: '#181825', accent: '#f472b6', background: '#0f0f17', card_bg: '#181824', text: '#ffffff' } },
    { id: 'yellow-gold', name: 'Amarelo Dourado VIP', icon: '💛', colors: { primary: '#eab308', secondary: '#1c1917', accent: '#fde047', background: '#0c0a09', card_bg: '#1c1917', text: '#ffffff' } },
    { id: 'emerald-green', name: 'Verde Esmeralda', icon: '🌿', colors: { primary: '#10b981', secondary: '#064e3b', accent: '#34d399', background: '#f0fdf4', card_bg: '#ffffff', text: '#065f46' } },
    { id: 'ocean-blue', name: 'Azul Oceano Profundo', icon: '🌊', colors: { primary: '#0284c7', secondary: '#0369a1', accent: '#38bdf8', background: '#f0f9ff', card_bg: '#ffffff', text: '#0c4a6e' } },
    { id: 'dark-gold', name: 'Midnight Dark Gold', icon: '🌌', colors: { primary: '#f59e0b', secondary: '#030712', accent: '#fbbf24', background: '#030712', card_bg: '#111827', text: '#f3f4f6' } },
    { id: 'galaxy-purple', name: 'Roxo Galáxia Gamer', icon: '🔮', colors: { primary: '#9333ea', secondary: '#090514', accent: '#c084fc', background: '#090514', card_bg: '#130b24', text: '#ffffff' } },
    { id: 'mint-clean', name: 'Menta Clean Minimalist', icon: '🍃', colors: { primary: '#059669', secondary: '#1e293b', accent: '#10b981', background: '#f4fbf7', card_bg: '#ffffff', text: '#0f172a' } },
    { id: 'sunset-coral', name: 'Sunset Coral Warm', icon: '🌅', colors: { primary: '#f97316', secondary: '#431407', accent: '#fb923c', background: '#fff7ed', card_bg: '#ffffff', text: '#431407' } },
    { id: 'sleek-black', name: 'Monocromático Sleek Black', icon: '🖤', colors: { primary: '#18181b', secondary: '#27272a', accent: '#52525b', background: '#fafafa', card_bg: '#ffffff', text: '#18181b' } },
    { id: 'electric-blue', name: 'Eletric Blue Neon', icon: '⚡', colors: { primary: '#00d2ff', secondary: '#080e1a', accent: '#3a7bd5', background: '#050914', card_bg: '#0e1626', text: '#ffffff' } },
    { id: 'rose-gold', name: 'Rose Gold Elegance', icon: '🍫', colors: { primary: '#fb7185', secondary: '#4c0519', accent: '#fda4af', background: '#fff1f2', card_bg: '#ffffff', text: '#4c0519' } },
    { id: 'indigo-corporate', name: 'Indigo Telecom Corporate', icon: '🫐', colors: { primary: '#4f46e5', secondary: '#1e1b4b', accent: '#6366f1', background: '#eef2ff', card_bg: '#ffffff', text: '#1e1b4b' } },
    { id: 'lime-energy', name: 'Lima Energético', icon: '🍏', colors: { primary: '#84cc16', secondary: '#1a2e05', accent: '#a3e635', background: '#f7fee7', card_bg: '#ffffff', text: '#1a2e05' } },
    { id: 'titanium-steel', name: 'Titanium Steel Minimal', icon: '🏙️', colors: { primary: '#475569', secondary: '#0f172a', accent: '#64748b', background: '#f1f5f9', card_bg: '#ffffff', text: '#0f172a' } },
    { id: 'matrix-violet', name: 'Violeta Matrix Dark', icon: '🍇', colors: { primary: '#8b5cf6', secondary: '#0f0c1b', accent: '#a78bfa', background: '#0f0c1b', card_bg: '#18142a', text: '#ffffff' } },
    { id: 'classic-bronze', name: 'Bronze Clássico', icon: '🏜️', colors: { primary: '#d97706', secondary: '#451a03', accent: '#f59e0b', background: '#fffbeb', card_bg: '#ffffff', text: '#451a03' } },
    { id: 'arctic-ice', name: 'Gelo Ártico', icon: '❄️', colors: { primary: '#38bdf8', secondary: '#0f172a', accent: '#7dd3fc', background: '#f0f9ff', card_bg: '#ffffff', text: '#0369a1' } },
    { id: 'lava-red', name: 'Lava Red Dark', icon: '🌋', colors: { primary: '#ef4444', secondary: '#0f0505', accent: '#f87171', background: '#0f0505', card_bg: '#1d0a0a', text: '#ffffff' } },
    { id: 'pine-forest', name: 'Floresta Pinheiro', icon: '🌲', colors: { primary: '#15803d', secondary: '#052e16', accent: '#22c55e', background: '#f0fdf4', card_bg: '#ffffff', text: '#052e16' } }
  ];

  const applyColorPreset = (presetId: string) => {
    const target = COLOR_PRESETS.find((p) => p.id === presetId);
    if (target) {
      setColors((prev) => ({ ...prev, ...target.colors }));
      toast.success(`Paleta "${target.name}" aplicada!`);
    }
  };

  // Manipulação de FAQ (Perguntas Frequentes)
  const handleAddFaq = () => {
    const newItem: FaqItem = {
      id: Date.now().toString(),
      pergunta: 'Nova Pergunta Frequente?',
      resposta: 'Escreva aqui a resposta completa para orientar seus clientes.'
    };
    setFaq([...faq, newItem]);
  };

  const handleDeleteFaq = (id: string) => {
    setFaq(faq.filter((item) => item.id !== id));
  };

  // Manipulação de Depoimentos
  const handleAddDepoimento = () => {
    const newItem: DepoimentoItem = {
      id: Date.now().toString(),
      nome: 'Nome do Cliente',
      cidade: 'Cidade - UF',
      foto: '',
      texto: 'Depoimento positivo sobre o sinal e a velocidade da fibra óptica.'
    };
    setDepoimentos([...depoimentos, newItem]);
  };

  const handleDeleteDepoimento = (id: string) => {
    setDepoimentos(depoimentos.filter((item) => item.id !== id));
  };

  // Manipulação de Benefícios
  const handleAddBeneficio = () => {
    const newItem: BeneficioItem = {
      id: Date.now().toString(),
      icone: 'Zap',
      titulo: 'Novo Benefício / Vantagem',
      descricao: 'Descrição breve dos diferenciais oferecidos pelo provedor.'
    };
    setBeneficios([...beneficios, newItem]);
  };

  const handleDeleteBeneficio = (id: string) => {
    setBeneficios(beneficios.filter((item) => item.id !== id));
  };



  // Upload de Arquivo ZIP de Template (Joomla/WordPress Style)
  const handleTemplateZipUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      toast.error('Por favor, selecione um arquivo no formato .zip');
      return;
    }

    const formData = new FormData();
    formData.append('template_zip', file);

    setUploadingZip(true);
    try {
      const toastId = toast.loading('Descompactando e instalando template...');
      const res: any = await api.post('/homepage/templates/upload', formData);
      toast.dismiss(toastId);

      if (res.success) {
        toast.success(res.message || 'Template instalado com sucesso!');
        fetchData();
      } else {
        toast.error(res.message || 'Erro ao carregar o template ZIP');
      }
    } catch (error) {
      console.error('Erro no upload do ZIP:', error);
      toast.error('Erro ao importar template ZIP');
    } finally {
      setUploadingZip(false);
    }
  };

  // Ativar Template
  const handleActivateTemplate = async (template: TemplateItem) => {
    try {
      const res: any = await api.post(`/homepage/templates/activate/${template.id}`, {
        name: template.name,
        type: template.type,
        version: template.version
      });

      if (res.success) {
        setActiveTemplate({ id: template.id, name: template.name, type: template.type });
        toast.success(`Template "${template.name}" ativado para a Homepage!`);
      } else {
        toast.error('Falha ao ativar o template');
      }
    } catch (error) {
      console.error('Erro ao ativar template:', error);
      toast.error('Erro ao ativar template');
    }
  };

  // Excluir Template Customizado
  const handleDeleteTemplate = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este template customizado?')) return;

    try {
      const res: any = await api.delete(`/homepage/templates/${id}`);
      if (res.success) {
        toast.success('Template excluído com sucesso!');
        fetchData();
      } else {
        toast.error(res.message || 'Erro ao excluir o template');
      }
    } catch (error) {
      toast.error('Erro ao excluir o template');
    }
  };

  // Alternar Exibição de Grupo no Site
  const handleToggleGrupoSite = async (grupoId: number) => {
    try {
      const res: any = await api.patch(`/planos-grupos/${grupoId}/toggle-site`);
      if (res.success) {
        setGrupos((prev) =>
          prev.map((g) => (g.id === grupoId ? { ...g, exibir_site: !g.exibir_site } : g))
        );
        toast.success(res.message || 'Status do grupo alterado');
      }
    } catch (error) {
      toast.error('Erro ao alterar exibição do grupo');
    }
  };

  // Alternar Exibição de Plano no Site
  const handleTogglePlanoSite = async (planoId: number) => {
    try {
      const res: any = await api.patch(`/planos/${planoId}/toggle-site`);
      if (res.success) {
        setPlanos((prev) =>
          prev.map((p) => (p.id === planoId ? { ...p, exibir_site: !p.exibir_site } : p))
        );
        toast.success(res.message || 'Status do plano no site alterado');
      }
    } catch (error) {
      toast.error('Erro ao alterar exibição do plano');
    }
  };

  // Adicionar Hero Slide
  const handleAddHero = () => {
    const newSlide: HeroSlide = {
      id: Date.now().toString(),
      badge: '🚀 NOVIDADE',
      titulo: 'Novo Destaque da Semana',
      subtitulo: 'Descrição curta da oferta ou promoção para os clientes.',
      cta_texto: 'Saiba Mais',
      cta_url: '#planos',
      cta_secundario_texto: 'Falar no WhatsApp',
      cta_secundario_url: '#contato',
      recurso_1: 'Sem Franquia de Dados',
      recurso_2: 'Instalação Ágil',
      recurso_3: 'Wi-Fi Grátis',
      card_header: 'Velocidade Máxima Conectada',
      card_speed: '1.000',
      card_unit: 'Mbps',
      card_subtext: '⚡ Ping ultra-baixo • 100% Fibra Dedicada',
      imagem_url: '',
      animar_imagem: false,
      animar_imagem_fundo: false,
      remover_gradiente_fundo: false,
      desativar_borda: false,
      estilo_fundo_card: 'padrao',
      inverter_lado: false,
      aumentar_tamanho: false,
      ativo: true,
      ordem: heros.length + 1
    };
    setHeros([...heros, newSlide]);
  };

  const deleteImageFile = async (url?: string) => {
    if (!url) return;
    try {
      await api.delete('/homepage/delete-image', { data: { url } });
    } catch (error) {
      console.error('Erro ao remover arquivo de imagem do servidor:', error);
    }
  };

  // Deletar Hero Slide
  const handleDeleteHero = (id: string) => {
    const heroToDelete = heros.find((h) => h.id === id);
    if (heroToDelete) {
      if (heroToDelete.imagem_url) deleteImageFile(heroToDelete.imagem_url);
      if (heroToDelete.imagem_fundo_url) deleteImageFile(heroToDelete.imagem_fundo_url);
    }
    const updatedHeros = heros.filter((h) => h.id !== id);
    setHeros(updatedHeros);
    handleSaveConfig({ heros: updatedHeros });
  };

  const getFullImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const baseUrl = apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
    const cleanPath = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${cleanPath}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Carregando dados da Homepage...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Botão Superior de Salvar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight="bold" color="primary">
            🌐 Customização do Website & Homepage
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Gerencie logos, conteúdos, planos visíveis e carregue novos templates no formato ZIP
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={() => handleSaveConfig()}
          disabled={saving}
          sx={{ fontWeight: 'bold', px: 3, borderRadius: 2 }}
        >
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </Box>

      {/* Navegação por Sub-Abas (Compactas) */}
      <Paper elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 38,
            '& .MuiTabs-flexContainer': { gap: 0.5 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.5,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 1.5,
              minWidth: 'auto',
              gap: 0.8
            }
          }}
        >
          <Tab icon={<BusinessIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Empresa & Header" />
          <Tab icon={<HeroIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Banners Hero" />
          <Tab icon={<WebIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Quem Somos" />
          <Tab icon={<StarIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Por Que Escolher" />
          <Tab icon={<FaqIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="FAQ (Perguntas)" />
          <Tab icon={<StarIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Depoimentos & Benefícios" />
          <Tab icon={<GroupsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Grupos & Planos" />
          <Tab icon={<ZipIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Templates (ZIP)" />
          <Tab icon={<PaletteIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Seções & Estilo" />
        </Tabs>
      </Paper>

      {/* ABA 0: Empresa & Header */}
      {tabValue === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Identificação da Empresa & Status do Site
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Alert severity="info" sx={{ mb: 2 }}>
                  ✨ O <strong>Nome da Empresa</strong>, a <strong>Logo Principal</strong>, o <strong>Slogan / Frase de Impacto</strong> e o <strong>Endereço Completo</strong> são configurados diretamente de <strong>Parâmetros Gerais</strong>.
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status do Website / Homepage</InputLabel>
                      <Select
                        value={info.status_site || 'ativo'}
                        label="Status do Website / Homepage"
                        onChange={(e) => setInfo({ ...info, status_site: e.target.value })}
                      >
                        <MenuItem value="ativo">🟢 Site Ativo (Online)</MenuItem>
                        <MenuItem value="inativo">🔴 Site Inativo (Desativado)</MenuItem>
                        <MenuItem value="manutencao">🛠️ Em Manutenção (Aviso no Site)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Contatos & Canais de Atendimento
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Telefone Fixo / Central"
                      fullWidth
                      size="small"
                      value={info.telefone}
                      onChange={(e) => setInfo({ ...info, telefone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="WhatsApp (Apenas Números com DDI)"
                      fullWidth
                      size="small"
                      placeholder="5511999999999"
                      value={info.whatsapp}
                      onChange={(e) => setInfo({ ...info, whatsapp: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Mensagem Padrão do WhatsApp ao Clicar"
                      fullWidth
                      size="small"
                      value={info.whatsapp_mensagem}
                      onChange={(e) => setInfo({ ...info, whatsapp_mensagem: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="E-mail de Contato"
                      fullWidth
                      size="small"
                      value={info.email}
                      onChange={(e) => setInfo({ ...info, email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Horário de Funcionamento / Atendimento"
                      fullWidth
                      size="small"
                      value={info.horario_atendimento}
                      onChange={(e) => setInfo({ ...info, horario_atendimento: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ABA 1: Banners Hero Slider */}
      {tabValue === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Slides de Destaque no Topo do Site ({(Array.isArray(heros) ? heros : []).length})
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddHero}>
              Adicionar Novo Slide
            </Button>
          </Box>

          {(Array.isArray(heros) ? heros : []).map((hero, index) => (
            <Card key={hero.id} elevation={2} sx={{ mb: 2, borderLeft: '4px solid #1976d2' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip label={`Slide #${index + 1}`} color="primary" size="small" />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={hero.ativo}
                          onChange={(e) =>
                            setHeros(heros.map((h) => (h.id === hero.id ? { ...h, ativo: e.target.checked } : h)))
                          }
                          size="small"
                        />
                      }
                      label={hero.ativo ? 'Exibindo no Site' : 'Oculto'}
                    />
                  </Box>
                  <IconButton color="error" size="small" onClick={() => handleDeleteHero(hero.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Etiqueta / Badge Superior"
                      fullWidth
                      size="small"
                      value={hero.badge}
                      onChange={(e) =>
                        setHeros(heros.map((h) => (h.id === hero.id ? { ...h, badge: e.target.value } : h)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Título Principal do Banner"
                      fullWidth
                      size="small"
                      value={hero.titulo}
                      onChange={(e) =>
                        setHeros(heros.map((h) => (h.id === hero.id ? { ...h, titulo: e.target.value } : h)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Subtítulo / Descrição"
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      value={hero.subtitulo}
                      onChange={(e) =>
                        setHeros(heros.map((h) => (h.id === hero.id ? { ...h, subtitulo: e.target.value } : h)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Texto Botão Primário"
                      fullWidth
                      size="small"
                      value={hero.cta_texto}
                      onChange={(e) =>
                        setHeros(heros.map((h) => (h.id === hero.id ? { ...h, cta_texto: e.target.value } : h)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Link Botão Primário"
                      fullWidth
                      size="small"
                      value={hero.cta_url}
                      onChange={(e) =>
                        setHeros(heros.map((h) => (h.id === hero.id ? { ...h, cta_url: e.target.value } : h)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Texto Botão Secundário"
                      fullWidth
                      size="small"
                      value={hero.cta_secundario_texto}
                      onChange={(e) =>
                        setHeros(
                          heros.map((h) => (h.id === hero.id ? { ...h, cta_secundario_texto: e.target.value } : h))
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Link Botão Secundário"
                      fullWidth
                      size="small"
                      value={hero.cta_secundario_url}
                      onChange={(e) =>
                        setHeros(
                          heros.map((h) => (h.id === hero.id ? { ...h, cta_secundario_url: e.target.value } : h))
                        )
                      }
                    />
                  </Grid>

                  {/* 3 Diferenciais / Badges Micro */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Diferencial 1 (Ex: Sem Franquia de Dados)"
                      fullWidth
                      size="small"
                      placeholder="Sem Franquia de Dados"
                      value={hero.recurso_1 ?? 'Sem Franquia de Dados'}
                      onChange={(e) =>
                        setHeros(heros.map((h) => (h.id === hero.id ? { ...h, recurso_1: e.target.value } : h)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Diferencial 2 (Ex: Instalação Ágil)"
                      fullWidth
                      size="small"
                      placeholder="Instalação Ágil"
                      value={hero.recurso_2 ?? 'Instalação Ágil'}
                      onChange={(e) =>
                        setHeros(heros.map((h) => (h.id === hero.id ? { ...h, recurso_2: e.target.value } : h)))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Diferencial 3 (Ex: Wi-Fi Grátis)"
                      fullWidth
                      size="small"
                      placeholder="Wi-Fi Grátis"
                      value={hero.recurso_3 ?? 'Wi-Fi Grátis'}
                      onChange={(e) =>
                        setHeros(heros.map((h) => (h.id === hero.id ? { ...h, recurso_3: e.target.value } : h)))
                      }
                    />
                  </Grid>

                  {/* Configuração da Mídia Lateral (Card Ilustrativo / Imagem de Destaque) */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #cbd5e1' }}>
                      <Typography variant="caption" fontWeight="bold" color="primary" display="block" mb={1.5}>
                        🎨 Mídia Lateral (Imagem de Destaque ou Card Ilustrativo)
                      </Typography>

                      <Grid container spacing={2}>
                        {/* Seção de Upload de Imagem de Destaque */}
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                            Imagem do Destaque Lateral do Banner (Se enviada, substitui o Card Ilustrativo)
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            {hero.imagem_url ? (
                              <Box
                                component="img"
                                src={getFullImageUrl(hero.imagem_url)}
                                alt="Banner"
                                sx={{ height: 56, width: 90, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', boxShadow: 1 }}
                                onClick={(e) => setHeroImgMenu({ anchor: e.currentTarget, heroId: hero.id })}
                              />
                            ) : (
                              <Box
                                onClick={(e) => setHeroImgMenu({ anchor: e.currentTarget, heroId: hero.id })}
                                sx={{
                                  width: 90, height: 56,
                                  border: '2px dashed #ccc',
                                  borderRadius: 1,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: 'pointer',
                                  bgcolor: 'background.paper',
                                  '&:hover': { borderColor: 'primary.main' }
                                }}
                              >
                                <PhotoCameraIcon color="disabled" />
                              </Box>
                            )}
                            <Button
                              variant="text"
                              size="small"
                              onClick={(e) => setHeroImgMenu({ anchor: e.currentTarget, heroId: hero.id })}
                            >
                              {hero.imagem_url ? 'Alterar Imagem' : 'Adicionar Imagem'}
                            </Button>
                          </Box>
                          <Menu
                            anchorEl={heroImgMenu.anchor}
                            open={Boolean(heroImgMenu.anchor) && heroImgMenu.heroId === hero.id}
                            onClose={() => setHeroImgMenu({ anchor: null, heroId: null })}
                          >
                            <MenuItem onClick={() => {
                              setHeroImgMenu({ anchor: null, heroId: null });
                              openCropModal('Upload Imagem de Destaque do Banner', hero.imagem_url,
                                (url) => setHeros((prev) => prev.map((h) => (h.id === hero.id ? { ...h, imagem_url: url } : h))));
                            }}>
                              <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
                              <ListItemText>Alterar Imagem</ListItemText>
                            </MenuItem>
                            {hero.imagem_url && (
                              <MenuItem
                                onClick={() => {
                                  setHeroImgMenu({ anchor: null, heroId: null });
                                  if (hero.imagem_url) deleteImageFile(hero.imagem_url);
                                  const updatedHeros = heros.map((h) => (h.id === hero.id ? { ...h, imagem_url: '' } : h));
                                  setHeros(updatedHeros);
                                  handleSaveConfig({ heros: updatedHeros });
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                                <ListItemText>Remover Imagem</ListItemText>
                              </MenuItem>
                            )}
                          </Menu>
                        </Grid>

                        {/* Switches de Personalização da Mídia */}
                        <Grid item xs={12}>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={hero.animar_imagem ?? false}
                                  onChange={(e) =>
                                    setHeros(heros.map((h) => (h.id === hero.id ? { ...h, animar_imagem: e.target.checked } : h)))
                                  }
                                  size="small"
                                  color="primary"
                                />
                              }
                              label={<Typography variant="caption" fontWeight="bold">Ativar Animação (Balançar)</Typography>}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={hero.inverter_lado ?? false}
                                  onChange={(e) =>
                                    setHeros(heros.map((h) => (h.id === hero.id ? { ...h, inverter_lado: e.target.checked } : h)))
                                  }
                                  size="small"
                                  color="info"
                                />
                              }
                              label={<Typography variant="caption" fontWeight="bold" color="info.main">Inverter Lado (Imagem à Esquerda)</Typography>}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={hero.aumentar_tamanho ?? false}
                                  onChange={(e) =>
                                    setHeros(heros.map((h) => (h.id === hero.id ? { ...h, aumentar_tamanho: e.target.checked } : h)))
                                  }
                                  size="small"
                                  color="warning"
                                />
                              }
                              label={<Typography variant="caption" fontWeight="bold" color="warning.main">Aumentar Tamanho (+40%)</Typography>}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={hero.desativar_borda ?? false}
                                  onChange={(e) =>
                                    setHeros(heros.map((h) => (h.id === hero.id ? { ...h, desativar_borda: e.target.checked } : h)))
                                  }
                                  size="small"
                                  color="secondary"
                                />
                              }
                              label={<Typography variant="caption" fontWeight="bold">Remover Borda e Fundo (Transparente)</Typography>}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 0.5 }} />
                          <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" my={1}>
                            Configurações do Card Ilustrativo (Quando NÃO houver imagem de destaque):
                          </Typography>
                        </Grid>

                        {/* Campos de texto do Card Ilustrativo */}
                        <Grid item xs={12} md={3}>
                          <TextField
                            label="Título Superior do Card"
                            fullWidth
                            size="small"
                            placeholder="Velocidade Máxima Conectada"
                            value={hero.card_header ?? 'Velocidade Máxima Conectada'}
                            onChange={(e) =>
                              setHeros(heros.map((h) => (h.id === hero.id ? { ...h, card_header: e.target.value } : h)))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            label="Velocidade"
                            fullWidth
                            size="small"
                            placeholder="1.000"
                            value={hero.card_speed ?? '1.000'}
                            onChange={(e) =>
                              setHeros(heros.map((h) => (h.id === hero.id ? { ...h, card_speed: e.target.value } : h)))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            label="Unidade (Ex: Mbps)"
                            fullWidth
                            size="small"
                            placeholder="Mbps"
                            value={hero.card_unit ?? 'Mbps'}
                            onChange={(e) =>
                              setHeros(heros.map((h) => (h.id === hero.id ? { ...h, card_unit: e.target.value } : h)))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                          <TextField
                            label="Valor do Plano (Ex: R$ 99,90/mês)"
                            fullWidth
                            size="small"
                            placeholder="R$ 99,90/mês"
                            value={hero.card_price ?? ''}
                            onChange={(e) =>
                              setHeros(heros.map((h) => (h.id === hero.id ? { ...h, card_price: e.target.value } : h)))
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={2.5}>
                          <TextField
                            label="Rodapé / Subtexto"
                            fullWidth
                            size="small"
                            placeholder="⚡ Ping ultra-baixo • 100% Fibra Dedicada"
                            value={hero.card_subtext ?? '⚡ Ping ultra-baixo • 100% Fibra Dedicada'}
                            onChange={(e) =>
                              setHeros(heros.map((h) => (h.id === hero.id ? { ...h, card_subtext: e.target.value } : h)))
                            }
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Imagem de Fundo da Seção Hero (Background Tela Inteira)
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      {hero.imagem_fundo_url ? (
                        <Box
                          component="img"
                          src={getFullImageUrl(hero.imagem_fundo_url)}
                          alt="Background"
                          sx={{ height: 56, width: 90, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', boxShadow: 1 }}
                          onClick={(e) => setHeroFundoMenu({ anchor: e.currentTarget, heroId: hero.id })}
                        />
                      ) : (
                        <Box
                          onClick={(e) => setHeroFundoMenu({ anchor: e.currentTarget, heroId: hero.id })}
                          sx={{
                            width: 90, height: 56,
                            border: '2px dashed #ccc',
                            borderRadius: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: 'background.paper',
                            '&:hover': { borderColor: 'secondary.main' }
                          }}
                        >
                          <PhotoCameraIcon color="disabled" />
                        </Box>
                      )}
                      <Button
                        variant="text"
                        size="small"
                        onClick={(e) => setHeroFundoMenu({ anchor: e.currentTarget, heroId: hero.id })}
                      >
                        {hero.imagem_fundo_url ? 'Alterar Fundo' : 'Adicionar Fundo'}
                      </Button>
                    </Box>
                    <Menu
                      anchorEl={heroFundoMenu.anchor}
                      open={Boolean(heroFundoMenu.anchor) && heroFundoMenu.heroId === hero.id}
                      onClose={() => setHeroFundoMenu({ anchor: null, heroId: null })}
                    >
                      <MenuItem onClick={() => {
                        setHeroFundoMenu({ anchor: null, heroId: null });
                        openCropModal('Upload Imagem de Fundo (Background)', hero.imagem_fundo_url,
                          (url) => setHeros((prev) => prev.map((h) => (h.id === hero.id ? { ...h, imagem_fundo_url: url } : h))));
                      }}>
                        <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Alterar Fundo</ListItemText>
                      </MenuItem>
                      {hero.imagem_fundo_url && (
                        <MenuItem
                          onClick={() => {
                            setHeroFundoMenu({ anchor: null, heroId: null });
                            if (hero.imagem_fundo_url) deleteImageFile(hero.imagem_fundo_url);
                            const updatedHeros = heros.map((h) => (h.id === hero.id ? { ...h, imagem_fundo_url: '' } : h));
                            setHeros(updatedHeros);
                            handleSaveConfig({ heros: updatedHeros });
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                          <ListItemText>Remover Fundo</ListItemText>
                        </MenuItem>
                      )}
                    </Menu>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={hero.animar_imagem_fundo ?? false}
                            onChange={(e) =>
                              setHeros(heros.map((h) => (h.id === hero.id ? { ...h, animar_imagem_fundo: e.target.checked } : h)))
                            }
                            size="small"
                            color="primary"
                          />
                        }
                        label={<Typography variant="caption" fontWeight="bold">Ativar Animação (Fundo Balançando)</Typography>}
                        sx={{ mt: 0.5 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={hero.remover_gradiente_fundo ?? false}
                            onChange={(e) =>
                              setHeros(heros.map((h) => (h.id === hero.id ? { ...h, remover_gradiente_fundo: e.target.checked } : h)))
                            }
                            size="small"
                            color="secondary"
                          />
                        }
                        label={<Typography variant="caption" fontWeight="bold" color="secondary.main">Remover Gradiente Translúcido de Fundo</Typography>}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* ABA 2: Quem Somos (História & Apresentação Institucional) */}
      {tabValue === 2 && (
        <Card elevation={1}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
              🏢 Seção Quem Somos / Apresentação Institucional
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Configure os textos institucionais, a imagem lateral de destaque e os números estatísticos exibidos no site.
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Badge Superior (Ex: QUEM SOMOS)"
                  fullWidth
                  size="small"
                  value={sobre.badge || ''}
                  onChange={(e) => setSobre({ ...sobre, badge: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  label="Título Principal da Seção"
                  fullWidth
                  size="small"
                  value={sobre.titulo || ''}
                  onChange={(e) => setSobre({ ...sobre, titulo: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Subtítulo de Destaque / Frase Forte"
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  value={sobre.subtitulo || ''}
                  onChange={(e) => setSobre({ ...sobre, subtitulo: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Texto Institucional Completo (História & Missão)"
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  value={sobre.texto || ''}
                  onChange={(e) => setSobre({ ...sobre, texto: e.target.value })}
                />
              </Grid>

              {/* Upload da Imagem Lateral do Quem Somos */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                  Imagem Lateral de Destaque da Seção Quem Somos
                </Typography>
                <Box display="flex" alignItems="center" gap={1.5}>
                  {sobre.imagem_url ? (
                    <Box
                      component="img"
                      src={getFullImageUrl(sobre.imagem_url)}
                      alt="Quem Somos"
                      sx={{ height: 56, width: 90, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', boxShadow: 1 }}
                      onClick={(e) => setSobreMenu(e.currentTarget)}
                    />
                  ) : (
                    <Box
                      onClick={(e) => setSobreMenu(e.currentTarget)}
                      sx={{
                        width: 90, height: 56,
                        border: '2px dashed #ccc',
                        borderRadius: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                        bgcolor: 'background.paper',
                        '&:hover': { borderColor: 'primary.main' }
                      }}
                    >
                      <PhotoCameraIcon color="disabled" />
                    </Box>
                  )}
                  <Button variant="text" size="small" onClick={(e) => setSobreMenu(e.currentTarget)}>
                    {sobre.imagem_url ? 'Alterar Imagem' : 'Adicionar Imagem'}
                  </Button>
                </Box>
                <Menu anchorEl={sobreMenu} open={Boolean(sobreMenu)} onClose={() => setSobreMenu(null)}>
                  <MenuItem onClick={() => {
                    setSobreMenu(null);
                    openCropModal('Upload Imagem Quem Somos', sobre.imagem_url, (url) => setSobre((prev) => ({ ...prev, imagem_url: url })));
                  }}>
                    <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Alterar Imagem</ListItemText>
                  </MenuItem>
                  {sobre.imagem_url && (
                    <MenuItem onClick={() => {
                      setSobreMenu(null);
                      if (sobre.imagem_url) deleteImageFile(sobre.imagem_url);
                      const updatedSobre = { ...sobre, imagem_url: '' };
                      setSobre(updatedSobre);
                      handleSaveConfig({ sobre: updatedSobre });
                    }} sx={{ color: 'error.main' }}>
                      <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                      <ListItemText>Remover Imagem</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" fontWeight="bold" color="primary" display="block">
                  📊 Indicadores e Estatísticas em Destaque (3 Caixas):
                </Typography>
              </Grid>

              {/* Estatística 1 */}
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" fontWeight="bold" display="block" mb={1}>Estatística #1</Typography>
                  <TextField
                    label="Valor / Número (Ex: 99.9%)"
                    fullWidth
                    size="small"
                    sx={{ mb: 1.5 }}
                    value={sobre.estatistica_1_numero || ''}
                    onChange={(e) => setSobre({ ...sobre, estatistica_1_numero: e.target.value })}
                  />
                  <TextField
                    label="Legenda / Rótulo"
                    fullWidth
                    size="small"
                    value={sobre.estatistica_1_rotulo || ''}
                    onChange={(e) => setSobre({ ...sobre, estatistica_1_rotulo: e.target.value })}
                  />
                </Paper>
              </Grid>

              {/* Estatística 2 */}
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" fontWeight="bold" display="block" mb={1}>Estatística #2</Typography>
                  <TextField
                    label="Valor / Número (Ex: +10 Mil)"
                    fullWidth
                    size="small"
                    sx={{ mb: 1.5 }}
                    value={sobre.estatistica_2_numero || ''}
                    onChange={(e) => setSobre({ ...sobre, estatistica_2_numero: e.target.value })}
                  />
                  <TextField
                    label="Legenda / Rótulo"
                    fullWidth
                    size="small"
                    value={sobre.estatistica_2_rotulo || ''}
                    onChange={(e) => setSobre({ ...sobre, estatistica_2_rotulo: e.target.value })}
                  />
                </Paper>
              </Grid>

              {/* Estatística 3 */}
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" fontWeight="bold" display="block" mb={1}>Estatística #3</Typography>
                  <TextField
                    label="Valor / Número (Ex: 100%)"
                    fullWidth
                    size="small"
                    sx={{ mb: 1.5 }}
                    value={sobre.estatistica_3_numero || ''}
                    onChange={(e) => setSobre({ ...sobre, estatistica_3_numero: e.target.value })}
                  />
                  <TextField
                    label="Legenda / Rótulo"
                    fullWidth
                    size="small"
                    value={sobre.estatistica_3_rotulo || ''}
                    onChange={(e) => setSobre({ ...sobre, estatistica_3_rotulo: e.target.value })}
                  />
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ABA 3: Por Que Escolher (Seção Ilustrada com 4 Vantagens) */}
      {tabValue === 3 && (
        <Card elevation={1} sx={{ borderLeft: '4px solid #ea580c' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" color="warning.dark">
              🚀 Seção "Por Que Escolher" (Ilustração Central & Diferenciais)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Edite o título, os 4 cards de vantagens, a cor de fundo e faça o upload da imagem da moça ilustrada no centro da seção.
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Título Principal da Seção"
                  fullWidth
                  size="small"
                  helperText="Use {EMPRESA} para substituir dinamicamente pelo nome do seu provedor"
                  value={beneficiosSection.titulo || ''}
                  onChange={(e) => setBeneficiosSection({ ...beneficiosSection, titulo: e.target.value })}
                />
              </Grid>

              {/* Seletor de Cor de Fundo da Seção */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" fontWeight="bold" color="warning.dark" display="block" gutterBottom>
                  🎨 Cor de Fundo da Seção
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <input
                    type="color"
                    value={(colors as any).why_choose_bg || '#ea580c'}
                    onChange={(e) => setColors({ ...colors, why_choose_bg: e.target.value } as any)}
                    style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="#ea580c"
                    value={(colors as any).why_choose_bg || ''}
                    onChange={(e) => setColors({ ...colors, why_choose_bg: e.target.value } as any)}
                  />
                </Box>
              </Grid>

              {/* Upload da Imagem Central da Moça */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                  Imagem Central em Destaque (Fundo Transparente PNG Recomendado)
                </Typography>
                <Box display="flex" alignItems="center" gap={1.5}>
                  {beneficiosSection.imagem_url ? (
                    <Box
                      component="img"
                      src={getFullImageUrl(beneficiosSection.imagem_url)}
                      alt="Moça Central"
                      sx={{ height: 56, width: 90, objectFit: 'contain', borderRadius: 1, cursor: 'pointer', boxShadow: 1 }}
                      onClick={(e) => setBeneficiosMenu(e.currentTarget)}
                    />
                  ) : (
                    <Box
                      onClick={(e) => setBeneficiosMenu(e.currentTarget)}
                      sx={{
                        width: 90, height: 56,
                        border: '2px dashed #ccc',
                        borderRadius: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                        bgcolor: 'background.paper',
                        '&:hover': { borderColor: 'warning.main' }
                      }}
                    >
                      <PhotoCameraIcon color="disabled" />
                    </Box>
                  )}
                  <Button variant="text" size="small" onClick={(e) => setBeneficiosMenu(e.currentTarget)}>
                    {beneficiosSection.imagem_url ? 'Alterar Imagem' : 'Adicionar Imagem'}
                  </Button>
                </Box>
                <Menu anchorEl={beneficiosMenu} open={Boolean(beneficiosMenu)} onClose={() => setBeneficiosMenu(null)}>
                  <MenuItem onClick={() => {
                    setBeneficiosMenu(null);
                    openCropModal('Upload Imagem Central (Por Que Escolher)', beneficiosSection.imagem_url,
                      (url) => setBeneficiosSection((prev) => ({ ...prev, imagem_url: url })));
                  }}>
                    <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Alterar Imagem</ListItemText>
                  </MenuItem>
                  {beneficiosSection.imagem_url && (
                    <MenuItem onClick={() => {
                      setBeneficiosMenu(null);
                      if (beneficiosSection.imagem_url) deleteImageFile(beneficiosSection.imagem_url);
                      const updatedBeneficiosSection = { ...beneficiosSection, imagem_url: '' };
                      setBeneficiosSection(updatedBeneficiosSection);
                      handleSaveConfig({ beneficios_section: updatedBeneficiosSection });
                    }} sx={{ color: 'error.main' }}>
                      <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                      <ListItemText>Remover Imagem</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </Grid>

              {/* 4 CARDS DE VANTAGENS */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff8f0' }}>
                  <Typography variant="caption" fontWeight="bold" color="warning.dark" display="block" mb={1}>
                    🚀 Card 1 (Esquerda Superior)
                  </Typography>
                  <TextField
                    label="Título 1"
                    fullWidth
                    size="small"
                    sx={{ mb: 1 }}
                    value={beneficiosSection.beneficio_1_titulo || ''}
                    onChange={(e) => setBeneficiosSection({ ...beneficiosSection, beneficio_1_titulo: e.target.value })}
                  />
                  <TextField
                    label="Descrição 1"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={beneficiosSection.beneficio_1_desc || ''}
                    onChange={(e) => setBeneficiosSection({ ...beneficiosSection, beneficio_1_desc: e.target.value })}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff8f0' }}>
                  <Typography variant="caption" fontWeight="bold" color="warning.dark" display="block" mb={1}>
                    🎧 Card 2 (Esquerda Inferior)
                  </Typography>
                  <TextField
                    label="Título 2"
                    fullWidth
                    size="small"
                    sx={{ mb: 1 }}
                    value={beneficiosSection.beneficio_2_titulo || ''}
                    onChange={(e) => setBeneficiosSection({ ...beneficiosSection, beneficio_2_titulo: e.target.value })}
                  />
                  <TextField
                    label="Descrição 2"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={beneficiosSection.beneficio_2_desc || ''}
                    onChange={(e) => setBeneficiosSection({ ...beneficiosSection, beneficio_2_desc: e.target.value })}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff8f0' }}>
                  <Typography variant="caption" fontWeight="bold" color="warning.dark" display="block" mb={1}>
                    ⚡ Card 3 (Direita Superior)
                  </Typography>
                  <TextField
                    label="Título 3"
                    fullWidth
                    size="small"
                    sx={{ mb: 1 }}
                    value={beneficiosSection.beneficio_3_titulo || ''}
                    onChange={(e) => setBeneficiosSection({ ...beneficiosSection, beneficio_3_titulo: e.target.value })}
                  />
                  <TextField
                    label="Descrição 3"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={beneficiosSection.beneficio_3_desc || ''}
                    onChange={(e) => setBeneficiosSection({ ...beneficiosSection, beneficio_3_desc: e.target.value })}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff8f0' }}>
                  <Typography variant="caption" fontWeight="bold" color="warning.dark" display="block" mb={1}>
                    📺 Card 4 (Direita Inferior)
                  </Typography>
                  <TextField
                    label="Título 4"
                    fullWidth
                    size="small"
                    sx={{ mb: 1 }}
                    value={beneficiosSection.beneficio_4_titulo || ''}
                    onChange={(e) => setBeneficiosSection({ ...beneficiosSection, beneficio_4_titulo: e.target.value })}
                  />
                  <TextField
                    label="Descrição 4"
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={beneficiosSection.beneficio_4_desc || ''}
                    onChange={(e) => setBeneficiosSection({ ...beneficiosSection, beneficio_4_desc: e.target.value })}
                  />
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ABA 4: Perguntas Frequentes (FAQ) */}
      {tabValue === 4 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Perguntas Frequentes do Site ({faq.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cadastre as perguntas mais comuns dos seus clientes com respostas claras e objetivas.
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddFaq}>
              Adicionar Nova Pergunta
            </Button>
          </Box>

          {faq.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#fafafa' }}>
              <Typography color="text.secondary">
                Nenhuma pergunta cadastrada. Clique no botão acima para adicionar a primeira pergunta.
              </Typography>
            </Paper>
          ) : (
            faq.map((item, index) => (
              <Card key={item.id} elevation={1} sx={{ mb: 2, borderLeft: '4px solid #1976d2' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Chip label={`Pergunta #${index + 1}`} color="primary" size="small" />
                    <IconButton color="error" size="small" onClick={() => handleDeleteFaq(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Pergunta Frequente (Ex: Como funciona a instalação?)"
                        fullWidth
                        size="small"
                        value={item.pergunta}
                        onChange={(e) =>
                          setFaq(faq.map((f) => (f.id === item.id ? { ...f, pergunta: e.target.value } : f)))
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Resposta Completa Explicativa"
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        value={item.resposta}
                        onChange={(e) =>
                          setFaq(faq.map((f) => (f.id === item.id ? { ...f, resposta: e.target.value } : f)))
                        }
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      {/* ABA 5: Depoimentos & Benefícios */}
      {tabValue === 5 && (
        <Grid container spacing={3}>
          {/* Seção 1: Depoimentos de Clientes */}
          <Grid item xs={12} md={7}>
            <Card elevation={1}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Depoimentos de Clientes ({depoimentos.length})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avaliações e comentários reais exibidos na homepage.
                    </Typography>
                  </Box>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddDepoimento}>
                    Adicionar Depoimento
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {depoimentos.map((dep, index) => (
                  <Paper key={dep.id} elevation={1} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Chip label={`Depoimento #${index + 1}`} size="small" color="secondary" />
                      <IconButton color="error" size="small" onClick={() => handleDeleteDepoimento(dep.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Nome do Cliente"
                          fullWidth
                          size="small"
                          value={dep.nome}
                          onChange={(e) =>
                            setDepoimentos(
                              depoimentos.map((d) => (d.id === dep.id ? { ...d, nome: e.target.value } : d))
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Cidade / Estado"
                          fullWidth
                          size="small"
                          value={dep.cidade}
                          onChange={(e) =>
                            setDepoimentos(
                              depoimentos.map((d) => (d.id === dep.id ? { ...d, cidade: e.target.value } : d))
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                          Foto do Cliente (Avatar)
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          {dep.foto ? (
                            <Box
                              component="img"
                              src={getFullImageUrl(dep.foto)}
                              alt={dep.nome}
                              sx={{ height: 48, width: 48, borderRadius: '50%', objectFit: 'cover', cursor: 'pointer', boxShadow: 1 }}
                              onClick={(e) => setDepoimentoMenu({ anchor: e.currentTarget, depId: dep.id })}
                            />
                          ) : (
                            <Box
                              onClick={(e) => setDepoimentoMenu({ anchor: e.currentTarget, depId: dep.id })}
                              sx={{
                                width: 48, height: 48,
                                borderRadius: '50%',
                                border: '2px dashed #ccc',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer',
                                bgcolor: 'background.paper',
                                '&:hover': { borderColor: 'primary.main' }
                              }}
                            >
                              <PhotoCameraIcon color="disabled" fontSize="small" />
                            </Box>
                          )}
                          <Button
                            variant="text"
                            size="small"
                            onClick={(e) => setDepoimentoMenu({ anchor: e.currentTarget, depId: dep.id })}
                          >
                            {dep.foto ? 'Alterar Foto' : 'Adicionar Foto'}
                          </Button>
                        </Box>
                        <Menu
                          anchorEl={depoimentoMenu.anchor}
                          open={Boolean(depoimentoMenu.anchor) && depoimentoMenu.depId === dep.id}
                          onClose={() => setDepoimentoMenu({ anchor: null, depId: null })}
                        >
                          <MenuItem onClick={() => {
                            setDepoimentoMenu({ anchor: null, depId: null });
                            openCropModal('Upload Foto do Cliente', dep.foto,
                              (url) => setDepoimentos((prev) => prev.map((d) => (d.id === dep.id ? { ...d, foto: url } : d))),
                              1
                            );
                          }}>
                            <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
                            <ListItemText>Alterar Foto</ListItemText>
                          </MenuItem>
                          {dep.foto && (
                            <MenuItem
                              onClick={() => {
                                setDepoimentoMenu({ anchor: null, depId: null });
                                if (dep.foto) deleteImageFile(dep.foto);
                                const updatedDepoimentos = depoimentos.map((d) => (d.id === dep.id ? { ...d, foto: '' } : d));
                                setDepoimentos(updatedDepoimentos);
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                              <ListItemText>Remover Foto</ListItemText>
                            </MenuItem>
                          )}
                        </Menu>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Texto do Depoimento"
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={dep.texto}
                          onChange={(e) =>
                            setDepoimentos(
                              depoimentos.map((d) => (d.id === dep.id ? { ...d, texto: e.target.value } : d))
                            )
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Seção 2: Benefícios & Diferenciais da Fibra */}
          <Grid item xs={12} md={5}>
            <Card elevation={1}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      Benefícios & Diferenciais ({beneficios.length})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Destaques da tecnologia e atendimento.
                    </Typography>
                  </Box>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddBeneficio}>
                    Adicionar
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {beneficios.map((ben, index) => (
                  <Paper key={ben.id} elevation={1} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Chip label={`Benefício #${index + 1}`} size="small" />
                      <IconButton color="error" size="small" onClick={() => handleDeleteBeneficio(ben.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <TextField
                          label="Título do Benefício"
                          fullWidth
                          size="small"
                          value={ben.titulo}
                          onChange={(e) =>
                            setBeneficios(
                              beneficios.map((b) => (b.id === ben.id ? { ...b, titulo: e.target.value } : b))
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Descrição Curta"
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={ben.descricao}
                          onChange={(e) =>
                            setBeneficios(
                              beneficios.map((b) => (b.id === ben.id ? { ...b, descricao: e.target.value } : b))
                            )
                          }
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ABA 6: Exibição de Grupos & Planos */}
      {tabValue === 6 && (
        <Box>
          {/* Painel de Seleção Direta do Plano em Destaque */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                  🔥 Seleção do Plano em Destaque (Destaque Ampliado no Site)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  O plano selecionado receberá o selo <strong>MAIS POPULAR</strong> e será exibido em tamanho <strong>ampliado (maior)</strong> no site.
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 280, bgcolor: '#ffffff' }}>
                <InputLabel>⭐ Escolher Plano Destaque</InputLabel>
                <Select
                  value={sections.destaque_plano_id || ''}
                  onChange={(e) =>
                    setSections({ ...sections, destaque_plano_id: e.target.value ? Number(e.target.value) : null })
                  }
                  label="⭐ Escolher Plano Destaque"
                >
                  <MenuItem value="">
                    <em>Nenhum (Destaque Automático)</em>
                  </MenuItem>
                  {planos.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      🔥 {p.nome} - R$ {Number(p.valor).toFixed(2)} ({p.velocidade_download}MB)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {/* Opção da Ação do Botão Contratar */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" color="info.dark">
                  🔘 Ação do Botão Contratar nos Planos
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Configure o comportamento ao clicar no botão "Contratar" dos cards de planos da homepage.
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 280, bgcolor: '#ffffff' }}>
                <InputLabel>Botão Contratar</InputLabel>
                <Select
                  value={sections.opcao_botao_contratar || 'whatsapp'}
                  onChange={(e) =>
                    setSections({ ...sections, opcao_botao_contratar: e.target.value as any })
                  }
                  label="Botão Contratar"
                >
                  <MenuItem value="whatsapp">💬 WhatsApp (Abrir conversa)</MenuItem>
                  <MenuItem value="precadastro">📝 Pré Cadastro (Formulário no Site)</MenuItem>
                  <MenuItem value="desabilitado">🚫 Desabilitado (Ocultar Botão)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          {grupos.map((grupo) => {
            const planosDoGrupo = planos.filter((p) => p.grupo_id === grupo.id);

            return (
              <Accordion key={grupo.id} defaultExpanded sx={{ mb: 1.5, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        📁 Grupo: {grupo.titulo}
                      </Typography>
                      <Chip label={`${planosDoGrupo.length} planos`} size="small" variant="outlined" />
                    </Box>

                    {/* Switch de Exibição do Grupo */}
                    <Box display="flex" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
                      <Typography variant="body2" color={grupo.exibir_site ? 'success.main' : 'text.secondary'} fontWeight="bold">
                        {grupo.exibir_site ? 'Visível no Site' : 'Oculto no Site'}
                      </Typography>
                      <Switch
                        checked={Boolean(grupo.exibir_site)}
                        onChange={() => handleToggleGrupoSite(grupo.id)}
                        color="success"
                      />
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ bg: '#fafafa' }}>
                  <Divider sx={{ mb: 2 }} />
                  {planosDoGrupo.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum plano cadastrado neste grupo.
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {planosDoGrupo.map((plano) => {
                        const isFeatured = sections.destaque_plano_id === plano.id;

                        return (
                          <Grid item xs={12} md={6} lg={4} key={plano.id}>
                            <Paper
                              elevation={isFeatured ? 4 : 1}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: isFeatured
                                  ? '3px solid #f59e0b'
                                  : plano.exibir_site
                                    ? '2px solid #2e7d32'
                                    : '1px dashed #ccc',
                                backgroundColor: isFeatured ? '#fffde7' : plano.exibir_site ? '#f1f8e9' : '#ffffff',
                                transition: 'all 0.2s',
                                position: 'relative'
                              }}
                            >
                              {isFeatured && (
                                <Chip
                                  label="🔥 DESTAQUE"
                                  color="warning"
                                  size="small"
                                  sx={{ fontWeight: 'bold', position: 'absolute', top: -12, right: 16 }}
                                />
                              )}

                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {plano.nome}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ⚡ {plano.velocidade_download} Mega Download / {plano.velocidade_upload} Mega Upload
                                  </Typography>
                                </Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                  R$ {Number(plano.valor).toFixed(2)}/mês
                                </Typography>
                              </Box>

                              <Divider sx={{ my: 1 }} />

                              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} flexWrap="wrap" gap={1}>
                                <Chip
                                  label={plano.ativo ? 'Plano Ativo' : 'Inativo'}
                                  color={plano.ativo ? 'primary' : 'default'}
                                  size="small"
                                />

                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isFeatured}
                                      onChange={(e) =>
                                        setSections({
                                          ...sections,
                                          destaque_plano_id: e.target.checked ? plano.id : null
                                        })
                                      }
                                      color="warning"
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption" fontWeight="bold" color={isFeatured ? 'warning.dark' : 'text.secondary'}>
                                      {isFeatured ? '⭐ Em Destaque' : 'Destacar'}
                                    </Typography>
                                  }
                                />

                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={Boolean(plano.exibir_site)}
                                      onChange={() => handleTogglePlanoSite(plano.id)}
                                      color="success"
                                      size="small"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption" fontWeight="bold" color={plano.exibir_site ? 'success.main' : 'text.secondary'}>
                                      {plano.exibir_site ? 'Exibir' : 'Ocultar'}
                                    </Typography>
                                  }
                                />
                              </Box>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* ABA 7: Gerenciador de Templates (Upload ZIP) */}
      {tabValue === 7 && (
        <Box>
          <Alert severity="success" sx={{ mb: 3 }}>
            🎉 <strong>Gerenciamento Dinâmico de Templates:</strong> Você pode fazer o upload de qualquer template no formato <strong>.ZIP</strong> (semelhante ao WordPress/Joomla). O sistema descompacta e disponibiliza o tema imediatamente para ativação!
          </Alert>

          {/* Área de Upload de ZIP */}
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3, textAlign: 'center', backgroundColor: '#f5f7fa', border: '2px dashed #1976d2' }}>
            <ZipIcon sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Carregar Novo Template em Arquivo ZIP
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
              Selecione o arquivo .zip com a estrutura do template web desejado (contendo imagens, estilos, manifestos).
            </Typography>

            <Button
              variant="contained"
              component="label"
              size="large"
              startIcon={uploadingZip ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
              disabled={uploadingZip}
              sx={{ fontWeight: 'bold', px: 4, py: 1.2, borderRadius: 3 }}
            >
              {uploadingZip ? 'Descompactando Template...' : 'Selecionar Arquivo .ZIP do Template'}
              <input type="file" hidden accept=".zip" onChange={handleTemplateZipUpload} />
            </Button>
          </Paper>

          {/* Lista de Templates Disponíveis */}
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Templates Instalados ({templates.length})
          </Typography>

          <Grid container spacing={2}>
            {templates.map((tpl) => {
              const isSelected = activeTemplate.id === tpl.id;

              return (
                <Grid item xs={12} md={6} lg={4} key={tpl.id}>
                  <Card
                    elevation={isSelected ? 4 : 1}
                    sx={{
                      borderRadius: 3,
                      border: isSelected ? '3px solid #1976d2' : '1px solid #e0e0e0',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    {isSelected && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="TEMPLATE ATIVO"
                        color="primary"
                        sx={{ position: 'absolute', top: 12, right: 12, fontWeight: 'bold' }}
                      />
                    )}

                    <CardContent sx={{ p: 2.5 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {tpl.name}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ minHeight: 48, mb: 2 }}>
                        {tpl.description}
                      </Typography>

                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Chip
                          label={tpl.type === 'builtin' ? 'Nativo SysUp' : 'Upload ZIP (Customizado)'}
                          size="small"
                          color={tpl.type === 'builtin' ? 'default' : 'secondary'}
                        />
                        {tpl.version && <Typography variant="caption">v{tpl.version}</Typography>}
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Box display="flex" gap={1}>
                        <Button
                          variant={isSelected ? 'contained' : 'outlined'}
                          color={isSelected ? 'success' : 'primary'}
                          fullWidth
                          startIcon={isSelected ? <CheckIcon /> : <WebIcon />}
                          onClick={() => handleActivateTemplate(tpl)}
                        >
                          {isSelected ? 'Ativo Atualmente' : 'Ativar Template'}
                        </Button>

                        {tpl.type === 'custom' && (
                          <IconButton color="error" title="Excluir Template" onClick={() => handleDeleteTemplate(tpl.id)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* ABA 8: Seções & Estilo */}
      {tabValue === 8 && (
        <Grid container spacing={3}>
          {/* Seção de Cores do Tema */}
          <Grid item xs={12}>
            <Card elevation={2} sx={{ borderLeft: '4px solid #1976d2', borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                      🎨 Personalização de Cores do Tema & Paleta do Site
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Escolha as cores principais do seu website ou aplique uma paleta pronta com 1 clique.
                    </Typography>
                  </Box>

                  {/* Gallery of 25 Color Presets */}
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
                      Paletas Prontas ({COLOR_PRESETS.length} Combinações Profissionais):
                    </Typography>
                    <Box
                      display="flex"
                      gap={1}
                      flexWrap="wrap"
                      sx={{
                        maxHeight: 220,
                        overflowY: 'auto',
                        p: 1.5,
                        backgroundColor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      {COLOR_PRESETS.map((preset) => {
                        const isCurrent =
                          colors.primary === preset.colors.primary && colors.secondary === preset.colors.secondary;
                        return (
                          <Tooltip key={preset.id} title={preset.name} arrow placement="top">
                            <Box
                              onClick={() => applyColorPreset(preset.id)}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                p: 0.8,
                                px: 1.2,
                                borderRadius: '20px',
                                border: isCurrent ? '2px solid #1976d2' : '1px solid #cbd5e1',
                                backgroundColor: isCurrent ? '#e0f2fe' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: isCurrent ? '0 2px 8px rgba(25,118,210,0.25)' : 'none',
                                '&:hover': {
                                  transform: 'scale(1.08)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  backgroundColor: preset.colors.primary,
                                  border: '1px solid rgba(0,0,0,0.15)'
                                }}
                              />
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  backgroundColor: preset.colors.secondary,
                                  border: '1px solid rgba(0,0,0,0.15)'
                                }}
                              />
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  backgroundColor: preset.colors.accent,
                                  border: '1px solid rgba(0,0,0,0.15)'
                                }}
                              />
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Cor Primária (Botões e Destaques)
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.primary || '#2563eb'}
                        onChange={(e) => setColors({ ...colors, primary: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={colors.primary || ''}
                        onChange={(e) => setColors({ ...colors, primary: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Cor Secundária (Botões de Apoio)
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.secondary || '#0f172a'}
                        onChange={(e) => setColors({ ...colors, secondary: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={colors.secondary || ''}
                        onChange={(e) => setColors({ ...colors, secondary: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Cor de Destaque / Badges
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.accent || '#3b82f6'}
                        onChange={(e) => setColors({ ...colors, accent: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={colors.accent || ''}
                        onChange={(e) => setColors({ ...colors, accent: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Cor de Fundo da Página (Background)
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.background || '#f8fafc'}
                        onChange={(e) => setColors({ ...colors, background: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={colors.background || ''}
                        onChange={(e) => setColors({ ...colors, background: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Cor de Fundo dos Cards
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.card_bg || '#ffffff'}
                        onChange={(e) => setColors({ ...colors, card_bg: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={colors.card_bg || ''}
                        onChange={(e) => setColors({ ...colors, card_bg: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Cor do Texto Principal
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.text || '#0f172a'}
                        onChange={(e) => setColors({ ...colors, text: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={colors.text || ''}
                        onChange={(e) => setColors({ ...colors, text: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  {/* Divisor e Cores Específicas por Seção */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" fontWeight="bold" color="primary" display="block" gutterBottom sx={{ mt: 1 }}>
                      📍 Cores de Fundo Personalizadas para Seções Específicas (Opcional):
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Fundo do Menu Topo (Header)
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.nav_bg || '#ffffff'}
                        onChange={(e) => setColors({ ...colors, nav_bg: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Ex: #ffffff"
                        value={colors.nav_bg || ''}
                        onChange={(e) => setColors({ ...colors, nav_bg: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Fundo da Seção de Planos
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.plans_bg || '#f8fafc'}
                        onChange={(e) => setColors({ ...colors, plans_bg: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Ex: #f8fafc"
                        value={colors.plans_bg || ''}
                        onChange={(e) => setColors({ ...colors, plans_bg: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Fundo da Seção Depoimentos
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.testimonials_bg || '#f8fafc'}
                        onChange={(e) => setColors({ ...colors, testimonials_bg: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Ex: #f8fafc"
                        value={colors.testimonials_bg || ''}
                        onChange={(e) => setColors({ ...colors, testimonials_bg: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                      Fundo da Seção Perguntas (FAQ)
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={colors.faq_bg || '#ffffff'}
                        onChange={(e) => setColors({ ...colors, faq_bg: e.target.value })}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Ex: #ffffff"
                        value={colors.faq_bg || ''}
                        onChange={(e) => setColors({ ...colors, faq_bg: e.target.value })}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" fontWeight="bold" color="warning.dark" display="block" gutterBottom>
                      Fundo Seção "Por Que Escolher"
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <input
                        type="color"
                        value={(colors as any).why_choose_bg || '#ea580c'}
                        onChange={(e) => setColors({ ...colors, why_choose_bg: e.target.value } as any)}
                        style={{ width: 44, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Ex: #ea580c"
                        value={(colors as any).why_choose_bg || ''}
                        onChange={(e) => setColors({ ...colors, why_choose_bg: e.target.value } as any)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Ativação de Seções da Homepage
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sections.heros_enabled}
                        onChange={(e) => setSections({ ...sections, heros_enabled: e.target.checked })}
                      />
                    }
                    label="Banner Carousel / Hero Slider no Topo"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sections.speed_test_enabled}
                        onChange={(e) => setSections({ ...sections, speed_test_enabled: e.target.checked })}
                      />
                    }
                    label="Simulador de Velocidade / Verificação de Cobertura"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sections.planos_enabled}
                        onChange={(e) => setSections({ ...sections, planos_enabled: e.target.checked })}
                      />
                    }
                    label="Seção de Planos por Grupos"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sections.beneficios_enabled}
                        onChange={(e) => setSections({ ...sections, beneficios_enabled: e.target.checked })}
                      />
                    }
                    label="Seção de Benefícios & Vantagens da Fibra"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sections.depoimentos_enabled}
                        onChange={(e) => setSections({ ...sections, depoimentos_enabled: e.target.checked })}
                      />
                    }
                    label="Seção de Depoimentos de Clientes"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sections.faq_enabled}
                        onChange={(e) => setSections({ ...sections, faq_enabled: e.target.checked })}
                      />
                    }
                    label="Seção de Perguntas Frequentes (FAQ)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sections.app_section_enabled ?? true}
                        onChange={(e) => setSections({ ...sections, app_section_enabled: e.target.checked })}
                      />
                    }
                    label="Seção do Aplicativo do Cliente / Central do Assinante"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sections.whatsapp_float_enabled}
                        onChange={(e) => setSections({ ...sections, whatsapp_float_enabled: e.target.checked })}
                      />
                    }
                    label="Botão Flutuante de WhatsApp no Canto da Tela"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={1}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                  Estilos CSS Customizados
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Adicione código CSS personalizado para alterar cores, fontes, margens ou animações do seu site.
                </Typography>

                <TextField
                  multiline
                  rows={10}
                  fullWidth
                  variant="outlined"
                  placeholder="/* Digite seu CSS aqui Exemplo: */&#10;.hero-title { color: #ff0055; }"
                  value={customCss}
                  onChange={(e) => setCustomCss(e.target.value)}
                  sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Modal de Crop de Imagem (Estilo Parâmetros Geral) */}
      <ImageUploadCrop
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        onImageSave={handleCropImageSave}
        title={cropTarget?.title || 'Upload e Edição de Imagem'}
        aspectRatio={cropTarget?.aspectRatio}
      />
    </Box>
  );
};

export default ParametrosHomepage;
